// client/src/pages/ProductDetailPage.js
import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Container, Grid, Box, Typography, Chip, Button, TextField, Rating, Divider,
  Card, CardContent, Select, MenuItem, FormControl, InputLabel, Stack, CircularProgress, Alert,
  Breadcrumbs, Paper, ToggleButtonGroup, ToggleButton, Accordion, AccordionSummary, AccordionDetails,
  Drawer, Checkbox, Table, TableHead, TableRow, TableCell, TableBody, Avatar,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import VerifiedIcon from '@mui/icons-material/Verified';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StraightenIcon from '@mui/icons-material/Straighten';
import CloseIcon from '@mui/icons-material/Close';
import { CartContext } from '../context/CartContext';
import { AuthContext } from '../context/AuthContext';
import ProductCarousel from '../components/ProductCarousel';
import ProductImageGallery from '../components/ProductImageGallery';
import { formatCurrency, getSavingsPercent } from '../utils/currency';
import { getImageUrl, API_BASE_URL, getProductImageUrl, NO_PHOTO_URL } from '../utils/media';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import { LOW_STOCK_THRESHOLD } from '../constants/inventory';
import { FREE_SHIPPING_THRESHOLD, ESTIMATED_DELIVERY_DAYS } from '../constants/shipping';
import { addRecentlyViewed } from '../utils/recentlyViewed';
import ProductQA from '../components/ProductQA';
import Seo from '../components/Seo';
import { AlsoNeeds, BackInStockForm, CompatibilityNotice, PreOwnedDetails, ProductVideo, SpecList } from '../components/ProductHobbyDetails';
import { useGeo } from '../context/GeoContext';

// Search engines and social apps need a full address for an image.
const absoluteImage = (imageUrl) => {
  const url = getImageUrl(imageUrl);
  if (!url) return undefined;
  return /^https?:/i.test(url) ? url : `${window.location.origin}${url}`;
};

const FAQ_ITEMS = [
  { q: 'What do RTF, BNF, PNP and ARF mean?', a: 'RTF (ready to fly) usually comes with everything needed, including the radio. BNF (bind and fly) needs your own compatible transmitter. PNP (plug and play) also needs your own receiver and usually a battery. ARF (almost ready to fly) needs some assembly and its own electronics. Check the Product Details section for exactly what is in the box.' },
  { q: 'What are your shipping times?', a: `Orders ship within 1–2 business days and typically arrive within ${ESTIMATED_DELIVERY_DAYS}. You'll get a tracking number by email as soon as it's on its way.` },
  { q: 'Can I return this if it isn\'t compatible with my model?', a: 'Yes — unused items in original packaging can be returned within 30 days for a full refund. See our Returns & Refunds policy for details.' },
  { q: 'Will this part work with my model?', a: 'Compatibility details are listed in the Product Details section above. If anything is unclear, use the questions section on this page or contact the seller via their store page before you order.' },
];

const SCALE_GUIDE_ROWS = [
  { scale: '1:72', typical: 'Small display kits', length: '14 cm' },
  { scale: '1:48', typical: 'Popular aircraft kits', length: '21 cm' },
  { scale: '1:32', typical: 'Large aircraft kits', length: '31 cm' },
  { scale: '1:24', typical: 'Large display kits', length: '42 cm' },
  { scale: '1:10', typical: 'Large RC models', length: '1 m' },
];

const ReviewForm = ({ productId, token, onReviewSubmit }) => {
  const [rating, setRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = new FormData();
      data.append('rating', rating);
      data.append('review_text', reviewText);
      photos.forEach((file) => data.append('images', file));
      const response = await fetch(`${API_BASE_URL}/api/products/${productId}/reviews`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: data,
      });
      if (!response.ok) throw new Error('Failed to submit review.');
      setReviewText('');
      setRating(5);
      setPhotos([]);
      onReviewSubmit();
    } catch (err) {
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card component="form" onSubmit={handleSubmit} variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>Leave a Review</Typography>
      <Rating value={rating} onChange={(e, val) => setRating(val)} sx={{ mb: 1 }} />
      <TextField
        fullWidth multiline minRows={3}
        placeholder="Share your thoughts on this product..."
        value={reviewText}
        onChange={(e) => setReviewText(e.target.value)}
        required
        sx={{ mb: 2 }}
      />
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button component="label" size="small" variant="outlined" startIcon={<AddPhotoAlternateOutlinedIcon />}>
          Add Photos
          <input hidden type="file" accept="image/*" multiple onChange={(e) => setPhotos(Array.from(e.target.files))} />
        </Button>
        {photos.map((file, i) => (
          <Chip key={i} label={file.name} size="small" onDelete={() => setPhotos((prev) => prev.filter((_, idx) => idx !== i))} />
        ))}
      </Stack>
      <Button type="submit" variant="contained" disabled={submitting}>Submit Review</Button>
    </Card>
  );
};

// Shared by the desktop inline panel and the mobile bottom sheet.
const VariantPicker = ({ colors, sizes, selectedColor, selectedSize, setSelectedColor, setSelectedSize, colorAvailable, sizeAvailableForColor }) => (
  <>
    {colors.length > 0 && (
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>Color{selectedColor ? `: ${selectedColor}` : ''}</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {colors.map((c) => {
            const available = colorAvailable(c);
            return (
              <Box
                key={c}
                onClick={() => available && setSelectedColor(c)}
                title={available ? c : `${c} (out of stock)`}
                sx={{
                  position: 'relative', width: 32, height: 32, borderRadius: '50%',
                  cursor: available ? 'pointer' : 'not-allowed',
                  bgcolor: c.toLowerCase(), border: '2px solid',
                  borderColor: selectedColor === c ? 'primary.main' : 'divider',
                  boxShadow: selectedColor === c ? 2 : 0,
                  opacity: available ? 1 : 0.35,
                  '&::after': !available ? {
                    content: '""', position: 'absolute', top: '50%', left: -2, right: -2, height: '2px',
                    bgcolor: 'text.disabled', transform: 'rotate(-45deg)',
                  } : undefined,
                }}
              />
            );
          })}
        </Stack>
      </Box>
    )}

    {sizes.length > 0 && (
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>Size / Scale{selectedSize ? `: ${selectedSize}` : ''}</Typography>
        <ToggleButtonGroup value={selectedSize} exclusive onChange={(e, v) => v && setSelectedSize(v)} size="small">
          {sizes.map((s) => {
            const available = sizeAvailableForColor(s);
            return (
              <ToggleButton
                key={s} value={s} disabled={!available}
                sx={!available ? { textDecoration: 'line-through', color: 'text.disabled' } : undefined}
              >
                {s}
              </ToggleButton>
            );
          })}
        </ToggleButtonGroup>
      </Box>
    )}
  </>
);

const FrequentlyBoughtTogether = ({ product, companions }) => {
  const { addToCart } = useContext(CartContext);
  const items = useMemo(() => [product, ...companions.slice(0, 2)], [product, companions]);
  const [checked, setChecked] = useState(() => items.map(() => true));

  useEffect(() => { setChecked(items.map(() => true)); }, [items]);

  const total = items.reduce((sum, item, i) => (checked[i] ? sum + Number(item.price) : sum), 0);
  const selectedCount = checked.filter(Boolean).length;

  const handleAddSelected = () => {
    items.forEach((item, i) => { if (checked[i]) addToCart(item, 1); });
  };

  if (companions.length === 0) return null;

  return (
    <Card variant="outlined" sx={{ mt: 6, p: 3 }}>
      <Typography variant="h6" fontWeight={700} gutterBottom>Frequently Bought Together</Typography>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ my: 2 }}>
        {items.map((item, i) => (
          <React.Fragment key={item.id}>
            <Stack alignItems="center" spacing={0.5} sx={{ width: 110 }}>
              <Box sx={{ position: 'relative' }}>
                <Box component="img" src={getProductImageUrl(item.imageUrl)} alt={item.name}
                  sx={{ width: 90, height: 90, objectFit: 'cover', borderRadius: 1, opacity: checked[i] ? 1 : 0.4 }} />
                <Checkbox
                  checked={checked[i]}
                  disabled={i === 0}
                  onChange={(e) => setChecked((prev) => prev.map((c, idx) => (idx === i ? e.target.checked : c)))}
                  sx={{ position: 'absolute', top: -8, left: -8, bgcolor: 'background.paper', borderRadius: '50%', p: 0.25 }}
                  size="small"
                />
              </Box>
              <Typography variant="caption" noWrap sx={{ maxWidth: 100 }}>{item.name}</Typography>
              <Typography variant="caption" fontWeight={600}>{formatCurrency(item.price)}</Typography>
            </Stack>
            {i < items.length - 1 && <Typography variant="h5" color="text.disabled">+</Typography>}
          </React.Fragment>
        ))}
      </Stack>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <Typography>
          Total for {selectedCount} item{selectedCount !== 1 ? 's' : ''}: <strong>{formatCurrency(total)}</strong>
        </Typography>
        <Button variant="contained" disabled={selectedCount === 0} onClick={handleAddSelected}>Add Selected to Cart</Button>
      </Stack>
    </Card>
  );
};

const IconButtonClose = ({ onClick }) => (
  <Button onClick={onClick} size="small" sx={{ minWidth: 0, p: 0.5 }}><CloseIcon fontSize="small" /></Button>
);

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { formatLocalPrice } = useGeo();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [productReviews, setProductReviews] = useState([]);
  const [sortOrder, setSortOrder] = useState('newest');
  const [ratingFilter, setRatingFilter] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [variantSheetOpen, setVariantSheetOpen] = useState(false);
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);
  const { addToCart } = useContext(CartContext);
  const { user, token } = useContext(AuthContext);

  const fetchProduct = useCallback(async (sort = 'newest') => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/products/${id}?sort=${sort}`);
      setProduct(response.data.product);
      setRelatedProducts(response.data.relatedProducts);
      setProductReviews(response.data.productReviews);
      addRecentlyViewed(response.data.product);
    } catch (err) {
      setError('Failed to fetch product details.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProduct(sortOrder);
    setSelectedColor(null);
    setSelectedSize(null);
    setQuantity(1);
    setRatingFilter(0);
  }, [fetchProduct, sortOrder]);

  const galleryImages = useMemo(() => {
    if (!product) return [];
    return [{ id: 'cover', image_url: product.imageUrl || NO_PHOTO_URL }, ...(product.images || [])];
  }, [product]);

  const variants = useMemo(() => product?.variants || [], [product]);
  const hasVariants = variants.length > 0;
  const colors = useMemo(() => [...new Set(variants.map((v) => v.color).filter(Boolean))], [variants]);
  const sizes = useMemo(() => [...new Set(variants.map((v) => v.size).filter(Boolean))], [variants]);

  const colorAvailable = (c) => variants.some((v) => v.color === c && Number(v.quantity) > 0);
  const sizeAvailableForColor = (s) => variants.some((v) => (colors.length === 0 || v.color === selectedColor) && v.size === s && Number(v.quantity) > 0);

  const selectedVariant = hasVariants
    ? variants.find((v) =>
        (colors.length === 0 || v.color === selectedColor) &&
        (sizes.length === 0 || v.size === selectedSize)
      )
    : null;

  const activeImage = selectedVariant?.image_url
    ? [{ id: 'variant', image_url: selectedVariant.image_url }, ...galleryImages]
    : galleryImages;

  const effectivePrice = selectedVariant?.price ?? product?.price;
  const effectiveQuantity = hasVariants
    ? (selectedVariant ? selectedVariant.quantity : 0)
    : Number(product?.quantity || 0);
  const savingsPercent = getSavingsPercent(effectivePrice, product?.compare_at_price);

  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (value >= 1 && value <= effectiveQuantity) setQuantity(value);
    else if (value > effectiveQuantity) setQuantity(effectiveQuantity);
  };

  const handleAddToCart = () => {
    if (hasVariants && !selectedVariant) return;
    addToCart(product, quantity, selectedVariant || null);
  };

  const handleBuyNow = () => {
    handleAddToCart();
    navigate('/cart');
  };

  const filteredReviews = ratingFilter
    ? productReviews.filter((r) => r.rating === ratingFilter)
    : productReviews;
  const ratingCounts = useMemo(() => {
    const counts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    productReviews.forEach((r) => { counts[r.rating] = (counts[r.rating] || 0) + 1; });
    return counts;
  }, [productReviews]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error) return <Container sx={{ py: 4 }}><Alert severity="error">{error}</Alert></Container>;
  if (!product) return <Container sx={{ py: 4 }}><Typography>Product not found.</Typography></Container>;

  const canAddToCart = hasVariants ? Boolean(selectedVariant) && effectiveQuantity > 0 : effectiveQuantity > 0;
  const pickerProps = { colors, sizes, selectedColor, selectedSize, setSelectedColor, setSelectedSize, colorAvailable, sizeAvailableForColor };

  return (
    <Container maxWidth="xl" sx={{ py: 4, pb: { xs: 12, md: 4 } }}>
      <Seo
        title={product.name}
        description={String(product.description || `Buy ${product.name}`).replace(/\s+/g, ' ').slice(0, 155)}
        canonicalPath={`/products/${product.id}`}
        image={absoluteImage(product.imageUrl)}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: product.name,
          description: product.description,
          image: absoluteImage(product.imageUrl),
          sku: String(product.id),
          ...(product.category ? { category: product.category } : {}),
          offers: {
            '@type': 'Offer',
            url: `${window.location.origin}/products/${product.id}`,
            priceCurrency: 'ZAR',
            price: Number(product.price).toFixed(2),
            availability: `https://schema.org/${hasVariants ? (variants.some((v) => Number(v.quantity) > 0) ? 'InStock' : 'OutOfStock') : (Number(product.quantity) > 0 ? 'InStock' : 'OutOfStock')}`,
          },
          ...(Number(product.product_review_count) > 0 ? {
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: Number(product.product_average_rating).toFixed(1),
              reviewCount: Number(product.product_review_count),
            },
          } : {}),
        }}
      />
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 3, overflowX: 'auto', whiteSpace: 'nowrap', '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' } }}
      >
        <Link to="/" style={{ color: 'inherit', textDecoration: 'none' }}>Home</Link>
        {product.category && (
          <Link to={`/?category=${encodeURIComponent(product.category)}`} style={{ color: 'inherit', textDecoration: 'none' }}>
            {product.category}
          </Link>
        )}
        {product.category && product.subcategory && (
          <Link
            to={`/?category=${encodeURIComponent(product.category)}&subcategory=${encodeURIComponent(product.subcategory)}`}
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            {product.subcategory}
          </Link>
        )}
        <Typography color="text.primary">{product.name}</Typography>
      </Breadcrumbs>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Box sx={{ position: { md: 'sticky' }, top: { md: 128 } }}>
            <ProductImageGallery images={activeImage} alt={product.name} />
            <ProductVideo url={product.video_url} title={product.name} />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          {product.category && (
            <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: '0.14em', display: 'block' }}>
              {product.subcategory ? `${product.category} / ${product.subcategory}` : product.category}
            </Typography>
          )}
          <Typography component="h1" variant="h3" fontWeight={800} letterSpacing="-0.02em" sx={{ fontSize: { xs: '1.9rem', md: '2.5rem' }, lineHeight: 1.1, mb: 1.5 }}>
            {product.name}
          </Typography>

          {product.store_name && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography color="text.secondary">
                Sold by <Link to={`/sellers/${product.store_name}`}>{product.store_name}</Link>
              </Typography>
              {product.seller_average_rating && (
                <Rating value={Number(product.seller_average_rating)} precision={0.5} size="small" readOnly />
              )}
            </Stack>
          )}

          {product.product_average_rating && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Rating value={Number(product.product_average_rating)} precision={0.5} readOnly />
              <Typography variant="body2" color="text.secondary">({product.product_review_count} reviews)</Typography>
            </Stack>
          )}

          <PreOwnedDetails product={product} />
          <CompatibilityNotice product={product} />

          <Paper
            variant="outlined"
            sx={{
              p: { xs: 2.5, md: 3.5 },
            }}
          >
            <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 1 }}>
              <Typography variant="h3" fontWeight={800} letterSpacing="-0.02em" sx={{ fontSize: { xs: '2rem', md: '2.4rem' } }}>
                {formatCurrency(effectivePrice)}
              </Typography>
              {savingsPercent && (
                <>
                  <Typography variant="h6" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                    {formatCurrency(product.compare_at_price)}
                  </Typography>
                  <Chip label={`Save ${savingsPercent}%`} color="error" size="small" />
                </>
              )}
            </Stack>
            {formatLocalPrice(effectivePrice) && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                ≈ {formatLocalPrice(effectivePrice)}
              </Typography>
            )}

            {/* Desktop: inline variant picker. Mobile: summary + "Select Options" opens a bottom sheet. */}
            {hasVariants && (
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <VariantPicker {...pickerProps} />
              </Box>
            )}
            {hasVariants && (
              <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
                <Button variant="outlined" fullWidth onClick={() => setVariantSheetOpen(true)}>
                  {selectedVariant ? `Options: ${[selectedColor, selectedSize].filter(Boolean).join(' / ')}` : 'Select Options'}
                </Button>
              </Box>
            )}
            {sizes.length > 0 && (
              <Button size="small" startIcon={<StraightenIcon fontSize="small" />} onClick={() => setSizeGuideOpen(true)} sx={{ mb: 2 }}>
                Scale Guide
              </Button>
            )}

            {hasVariants && !selectedVariant && (
              <Alert severity="info" sx={{ mb: 2, display: { xs: 'none', md: 'flex' } }}>
                Select {colors.length > 0 ? 'a color' : ''}{colors.length > 0 && sizes.length > 0 ? ' and ' : ''}{sizes.length > 0 ? 'a size' : ''} to see availability.
              </Alert>
            )}

            <AlsoNeeds product={product} />

            {canAddToCart ? (
              <>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
                  <TextField
                    type="number" label="Qty" size="small" value={quantity}
                    onChange={handleQuantityChange}
                    inputProps={{ min: 1, max: effectiveQuantity }}
                    sx={{ width: 90 }}
                  />
                  <Button variant="contained" size="large" onClick={handleAddToCart} sx={{ flexGrow: 1 }}>Add to Cart</Button>
                </Stack>
                <Button variant="outlined" size="large" fullWidth onClick={handleBuyNow} sx={{ mb: 1.5 }}>Buy Now</Button>
                <Typography variant="body2" color={effectiveQuantity <= LOW_STOCK_THRESHOLD ? 'error.main' : 'text.secondary'} fontWeight={effectiveQuantity <= LOW_STOCK_THRESHOLD ? 600 : 400}>
                  {effectiveQuantity <= LOW_STOCK_THRESHOLD ? `Only ${effectiveQuantity} left in stock` : `${effectiveQuantity} in stock`}
                </Typography>
              </>
            ) : (
              (!hasVariants || !variants.some((v) => Number(v.quantity) > 0)) && <BackInStockForm productId={product.id} />
            )}

            <Divider sx={{ my: 2.5 }} />
            <Grid container spacing={1.5}>
              {[
                { icon: <ScheduleOutlinedIcon fontSize="small" />, title: 'Delivery', text: `${ESTIMATED_DELIVERY_DAYS}` },
                { icon: <VerifiedIcon fontSize="small" />, title: '30-day returns', text: 'Full refund policy' },
                { icon: <LocalShippingOutlinedIcon fontSize="small" />, title: 'Free shipping', text: `Orders over ${formatCurrency(FREE_SHIPPING_THRESHOLD)}` },
              ].map((item) => (
                <Grid item xs={12} sm={4} md={12} lg={4} key={item.title}>
                  <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ p: 1.5, borderRadius: 3, bgcolor: 'action.hover', height: '100%' }}>
                    <Box sx={{ color: 'primary.main', mt: 0.25 }}>{item.icon}</Box>
                    <Box>
                      <Typography variant="body2" fontWeight={700}>{item.title}</Typography>
                      <Typography variant="caption" color="text.secondary">{item.text}</Typography>
                    </Box>
                  </Stack>
                </Grid>
              ))}
            </Grid>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              Price includes VAT. <Link to="/returns" style={{ color: 'inherit' }}>Returns &amp; refunds policy</Link>
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <FrequentlyBoughtTogether product={product} companions={relatedProducts} />

      {relatedProducts.length > 0 && (
        <Box sx={{ mt: 6 }}>
          <ProductCarousel title="Related Products" products={relatedProducts} />
        </Box>
      )}

      <Box sx={{ mt: 6 }}>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={700}>Product Details</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <SpecList product={product} />
            <Typography color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>{product.description}</Typography>
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={700}>Shipping &amp; Returns</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography color="text.secondary">
              Estimated delivery: {ESTIMATED_DELIVERY_DAYS}. Free shipping on orders over {formatCurrency(FREE_SHIPPING_THRESHOLD)};
              otherwise a flat shipping fee applies at checkout. Items can be returned within 30 days of delivery — see our{' '}
              <Link to="/returns">Returns &amp; Refunds policy</Link> for full details.
            </Typography>
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={700}>Frequently Asked Questions</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              {FAQ_ITEMS.map((item) => (
                <Box key={item.q}>
                  <Typography fontWeight={600} sx={{ mb: 0.5 }}>{item.q}</Typography>
                  <Typography variant="body2" color="text.secondary">{item.a}</Typography>
                </Box>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Box>

      <Box sx={{ mt: 6 }}>
        <ProductQA productId={product.id} sellerId={product.seller_id} />
      </Box>

      <Box sx={{ mt: 6 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h5" fontWeight={700}>Customer Reviews</Typography>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Sort by</InputLabel>
            <Select label="Sort by" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
              <MenuItem value="newest">Most Recent</MenuItem>
              <MenuItem value="highest">Highest Rating</MenuItem>
              <MenuItem value="lowest">Lowest Rating</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        {productReviews.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <Chip label={`All (${productReviews.length})`} onClick={() => setRatingFilter(0)} color={ratingFilter === 0 ? 'primary' : 'default'} variant={ratingFilter === 0 ? 'filled' : 'outlined'} />
            {[5, 4, 3, 2, 1].filter((star) => ratingCounts[star] > 0).map((star) => (
              <Chip
                key={star} label={`${star}★ (${ratingCounts[star]})`}
                onClick={() => setRatingFilter(star)}
                color={ratingFilter === star ? 'primary' : 'default'}
                variant={ratingFilter === star ? 'filled' : 'outlined'}
              />
            ))}
          </Stack>
        )}

        {user && <ReviewForm productId={product.id} token={token} onReviewSubmit={() => fetchProduct(sortOrder)} />}

        {filteredReviews.length > 0 ? (
          filteredReviews.map((review) => (
            <Card key={review.id} sx={{ mb: 2, borderRadius: 4, '&:hover': { transform: 'none', boxShadow: 'none' } }}>
              <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36, fontWeight: 700 }}>
                    {review.reviewer_email[0].toUpperCase()}
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" fontWeight={700}>{review.reviewer_email.split('@')[0]}</Typography>
                    <Rating value={review.rating} readOnly size="small" />
                  </Box>
                  {review.verified_purchase ? (
                    <Chip icon={<VerifiedIcon sx={{ fontSize: 14 }} />} label="Verified purchase" size="small" color="success" variant="outlined" />
                  ) : null}
                </Stack>
                <Typography sx={{ mb: 1.5, lineHeight: 1.7 }}>{review.review_text}</Typography>
                {review.images && review.images.length > 0 && (
                  <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
                    {review.images.map((img) => (
                      <Box key={img.id} component="a" href={getImageUrl(img.image_url)} target="_blank" rel="noopener noreferrer">
                        <Box component="img" src={getImageUrl(img.image_url)} alt="Review photo" sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 1 }} />
                      </Box>
                    ))}
                  </Stack>
                )}
                <Typography variant="caption" color="text.secondary">
                  {new Date(review.created_at).toLocaleDateString()}
                </Typography>
              </CardContent>
            </Card>
          ))
        ) : (
          <Typography color="text.secondary">
            {productReviews.length === 0 ? 'This product has no reviews yet. Be the first to leave one!' : 'No reviews match this filter.'}
          </Typography>
        )}
        <Divider sx={{ mt: 3 }} />
      </Box>

      {/* Sticky mobile add-to-cart bar */}
      <Paper
        elevation={4}
        sx={{
          display: { xs: 'flex', md: 'none' },
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 10,
          p: 1.5, alignItems: 'center', gap: 1.5,
        }}
      >
        <Typography variant="h6" fontWeight={700} sx={{ flexShrink: 0 }}>{formatCurrency(effectivePrice)}</Typography>
        <Button
          variant="contained" fullWidth
          disabled={hasVariants ? !canAddToCart : !canAddToCart}
          onClick={() => (hasVariants && !selectedVariant ? setVariantSheetOpen(true) : handleAddToCart())}
        >
          {hasVariants && !selectedVariant ? 'Select Options' : !canAddToCart ? 'Out of Stock' : 'Add to Cart'}
        </Button>
      </Paper>

      {/* Mobile variant picker bottom sheet */}
      <Drawer anchor="bottom" open={variantSheetOpen} onClose={() => setVariantSheetOpen(false)}>
        <Box sx={{ p: 3, maxHeight: '70vh', overflowY: 'auto' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6" fontWeight={700}>Select Options</Typography>
            <IconButtonClose onClick={() => setVariantSheetOpen(false)} />
          </Stack>
          <VariantPicker {...pickerProps} />
          <Button variant="contained" fullWidth disabled={!canAddToCart} onClick={() => setVariantSheetOpen(false)} sx={{ mt: 1 }}>
            Done
          </Button>
        </Box>
      </Drawer>

      {/* Scale guide bottom sheet */}
      <Drawer anchor="bottom" open={sizeGuideOpen} onClose={() => setSizeGuideOpen(false)}>
        <Box sx={{ p: 3, maxWidth: 480, mx: 'auto', width: '100%' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6" fontWeight={700}>Scale Guide</Typography>
            <IconButtonClose onClick={() => setSizeGuideOpen(false)} />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Scale shows how much smaller a model is than the real thing. As a guide, this is how long a 10 m aircraft would be at each scale. Check the Product Details section for exact dimensions.</Typography>
          <Table size="small">
            <TableHead>
              <TableRow><TableCell>Scale</TableCell><TableCell>Typical use</TableCell><TableCell>10 m aircraft</TableCell></TableRow>
            </TableHead>
            <TableBody>
              {SCALE_GUIDE_ROWS.map((row) => (
                <TableRow key={row.scale}>
                  <TableCell>{row.scale}</TableCell><TableCell>{row.typical}</TableCell><TableCell>{row.length}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Drawer>
    </Container>
  );
};

export default ProductDetailPage;
